import { Node, Path, Text } from 'slate';
import type { Editor, NodeEntry, Range } from 'slate';

import { getLineHeightPx } from './general';
import { leadingSpacesWidthInLinePx } from './line-measurements';
import { maxValueOf } from '../../util';
import type { RenderModelLineChild, RenderModelTab } from '../internal';
import {
  getIsRightAlignedTab,
  isDrawingElement,
  isRenderModelTab,
  isRenderModelText,
  isSignatureFieldElement,
  isTabElement,
  measureText,
  Queries,
} from '../internal';
import { isCheckboxOptionFieldElement, isLeafVoid, isLinkElement, lineType } from '../types';
import type {
  ListMarker,
  ParagraphChildElement,
  ParagraphElement,
  RenderModelBreak,
  RenderModelCheckboxOption,
  RenderModelInline,
  RenderModelLine,
  RenderModelLink,
} from '../types';

type RMLinePart = Omit<
  RenderModelLine,
  'heightPx' | 'isFirstLine' | 'isLastLine' | 'leadingSpacesWidthPx' | 'widthPx' | 'spaceWidthPx'
>;

export function buildRenderModelLine(
  editor: Editor,
  options: {
    isFirstLine: boolean;
    isJustified: boolean;
    isLastLine: boolean;
    listMarker: ListMarker | null;
    maxLineWidthPx: number;
    paragraph: ParagraphElement;
    range: Range;
    tabWidthsPx: number[];
  }
): RenderModelLine {
  const {
    isFirstLine,
    isJustified,
    isLastLine,
    listMarker,
    maxLineWidthPx,
    paragraph,
    range,
    tabWidthsPx,
  } = options;

  let { anchor } = range;
  const { focus } = range;

  /**
   * If we're building a non-first line and the anchor is at the end of some text node (but not in a
   * void element), try to move the anchor to the start of the next text node. This ensures that
   * hanging text nodes from the previous line don't contribute to this line's height.
   */
  if (!isFirstLine && Node.leaf(paragraph, anchor.path).text.length === anchor.offset) {
    const hasVoidAncestor = Path.ancestors(anchor.path).some(ancestorPath =>
      isLeafVoid(editor, Node.get(paragraph, ancestorPath))
    );

    if (!hasVoidAncestor) {
      const nextLeafEntry = Node.texts(paragraph, { from: anchor.path }).drop(1).next().value;
      if (nextLeafEntry) anchor = { offset: 0, path: nextLeafEntry[1] };
    }
  }

  const nodesInLine = buildRenderModelLineChildren({
    paragraph,
    range: { anchor, focus },
    tabWidthsPx,
  });

  const rMBreak = nodesInLine.find((node): node is RenderModelBreak => Queries.isActiveBreak(node));
  const baselinesAndDescents = nodesInLine.map(getMaxBaselineAndDescent);

  if (listMarker) {
    const { textBaselineToTopWithPaddingPx, textDescentPx } = Queries.textFontMetrics(listMarker, {
      withOriginalFontSize: true,
    });

    baselinesAndDescents.push({
      baselinePx: textBaselineToTopWithPaddingPx,
      descentPx: textDescentPx,
    });
  }

  const newLine: RMLinePart = {
    baselinePx: maxValueOf(baselinesAndDescents, 'baselinePx'),
    children: nodesInLine,
    contentBreak: rMBreak ? { variant: rMBreak.variant } : undefined,
    maxDescentPx: maxValueOf(baselinesAndDescents, 'descentPx'),
    relativeEndPoint: focus,
    relativeStartPoint: anchor,
    suggestedEdits: [],
    type: lineType,
  };

  incorrectlyExtendRightAlignedTabWidth(editor, newLine, { maxLineWidthPx });

  const lineSpacing = Queries.renderedParagraphLineSpacing(paragraph);
  const widthPx = Queries.nonJustifiedLineContentWidthPx(editor, newLine);
  const heightPx = getLineHeightPx(lineSpacing, newLine, listMarker);
  const leadingSpacesWidthPx = leadingSpacesWidthInLinePx(editor, newLine);

  const lineWithoutSpaceWidth: Omit<RenderModelLine, 'spaceWidthPx'> = {
    ...newLine,
    heightPx,
    isFirstLine,
    isLastLine,
    leadingSpacesWidthPx,
    widthPx,
  };

  const spaceWidthPx = isJustified
    ? calculateSpaceWidth(editor, lineWithoutSpaceWidth, { maxLineWidthPx })
    : 'default';

  return { ...lineWithoutSpaceWidth, spaceWidthPx };
}

/**
 * This method builds the render model children of a paragraph from the content
 * model children of a paragraph.
 *
 * - It includes only the nodes that are within the range.
 * - It attaches useful additional properties to the render nodes.
 * - For tabs, it adds a `width` property to the node
 *
 * This code is a modification of Slate's Node.fragment to extract only the
 * nodes we care about to build a line, and to attach extra props (e.g.,
 * relativeStartPoint, etc.) to some nodes.
 */
function buildRenderModelLineChildren(options: {
  paragraph: ParagraphElement;
  range: Range;
  tabWidthsPx: number[];
}): RenderModelLineChild[] {
  const {
    paragraph: root,
    range: { anchor, focus },
    tabWidthsPx,
  } = options;
  const remainingTabWidthsPx = tabWidthsPx.slice();

  return root.children.flatMap((node, index) => buildRenderModelDescendant(node, [index]) ?? []);

  function buildRenderModelDescendant(
    node: ParagraphChildElement,
    path: Path
  ): RenderModelLineChild | null {
    if (Path.isBefore(path, anchor.path) || Path.isAfter(path, focus.path)) return null;

    const key = Queries.findKey(node).id;
    const commonProps = { key, relativePath: path };

    if (Text.isText(node)) {
      const relativeStartPoint = Path.equals(anchor.path, path) ? anchor : { offset: 0, path };

      const relativeEndPoint = Path.equals(focus.path, path)
        ? focus
        : { offset: node.text.length, path };

      const { textBaselineToTopWithPaddingPx, textDescentPx } = Queries.textFontMetrics(node, {
        withOriginalFontSize: true,
      });

      return {
        ...node,
        ...commonProps,
        descentPx: textDescentPx,
        relativeEndPoint,
        relativeStartPoint,
        text: node.text.slice(relativeStartPoint.offset, relativeEndPoint.offset),
        topPx: textBaselineToTopWithPaddingPx,
      };
    }

    const children: RenderModelLineChild[] = node.children.flatMap(
      (node, index) => buildRenderModelDescendant(node, path.concat(index)) ?? []
    );

    if (isTabElement(node))
      return {
        ...node,
        ...commonProps,
        children: children as RenderModelTab['children'],
        tabWidthPx: remainingTabWidthsPx.shift() ?? 0,
      };

    if (isLinkElement(node))
      return {
        ...node,
        ...commonProps,
        children: children as RenderModelLink['children'],
      };

    if (isCheckboxOptionFieldElement(node))
      return {
        ...node,
        ...commonProps,
        children: children as RenderModelCheckboxOption['children'],
      };

    const narrowedChildren = children as Exclude<
      RenderModelInline,
      RenderModelTab | RenderModelLink | RenderModelCheckboxOption
    >['children'];

    return { ...node, ...commonProps, children: narrowedChildren };
  }
}

/**
 * Calculate the space width for each space in a line when the paragraph is justified.
 */
function calculateSpaceWidth(
  editor: Editor,
  line: Omit<RenderModelLine, 'spaceWidthPx'>,
  options: { maxLineWidthPx: number }
): RenderModelLine['spaceWidthPx'] {
  const { maxLineWidthPx } = options;

  let totalSpaceWidthPx = 0;
  let lineText = '';

  /**
   * Sum the widths of all spaces (including trailing spaces) while also aggregating the text
   * content of the line. We include trailing spaces because we subtract this from line.widthPx,
   * which also includes trailing spaces, to get the total width of non-space content.
   */
  for (const [textNode] of Node.texts({ ...line, spaceWidthPx: 'default' })) {
    const { text } = textNode;
    const spacesInText = text.split(' ').length - 1;

    lineText += text;

    totalSpaceWidthPx += measureText(editor, {
      suppressJustifiedWarning: true,
      text: ' '.repeat(spacesInText),
      textNode,
    });
  }

  const totalNumberOfNonTrailingSpaces = lineText.trimEnd().split(' ').length - 1;

  // If there are no non-trailing spaces, we can't justify the text
  if (totalNumberOfNonTrailingSpaces === 0) return 'default';

  const nonSpaceWidthPx = line.widthPx - totalSpaceWidthPx;
  const remainingWidthPx = maxLineWidthPx - nonSpaceWidthPx;

  return remainingWidthPx / totalNumberOfNonTrailingSpaces;
}

function getMaxBaselineAndDescent(node: RenderModelLineChild): {
  baselinePx: number;
  descentPx: number;
} {
  if (isRenderModelText(node)) return { baselinePx: node.topPx, descentPx: node.descentPx };

  if (isSignatureFieldElement(node))
    return { baselinePx: Queries.getSignatureImageDimensions(node).imgHeightPx, descentPx: 0 };

  if (isDrawingElement(node))
    return { baselinePx: Queries.getDrawingDimensions(node).containerHeightPx, descentPx: 0 };

  const childrenBaselinesAndDescents = node.children.map(getMaxBaselineAndDescent);

  return {
    baselinePx: maxValueOf(childrenBaselinesAndDescents, 'baselinePx'),
    descentPx: maxValueOf(childrenBaselinesAndDescents, 'descentPx'),
  };
}

/**
 * Finds the last right-aligned tab on the paragraph and extends its width as much as possible
 * without causing the line to overflow the paragraph. This behavior is incorrect, but happens to
 * be correct for right-aligned tabs in tables of contents. See EDI-431.
 */
function incorrectlyExtendRightAlignedTabWidth(
  editor: Editor,
  line: RMLinePart,
  options: { maxLineWidthPx: number }
) {
  const { maxLineWidthPx } = options;

  const lastRightAlignedTabEntry = Node.descendants(line as Node, { reverse: true }).find(
    (nodeEntry): nodeEntry is NodeEntry<RenderModelTab> =>
      isRenderModelTab(nodeEntry[0]) && getIsRightAlignedTab(nodeEntry[0])
  );

  if (!lastRightAlignedTabEntry) return;
  const [tab] = lastRightAlignedTabEntry;

  const widthWithoutTabPx = Queries.nonJustifiedLineContentWidthPx(editor, line) - tab.tabWidthPx;
  tab.tabWidthPx = Math.max(0, maxLineWidthPx - widthWithoutTabPx);
}
